import React, { useState, useEffect, useCallback } from 'react';
import {
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, Box, Chip, FormControl, InputLabel, Select, MenuItem, Alert,
  CircularProgress, Snackbar, Tooltip, IconButton, Typography, Stack,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LinkIcon from '@mui/icons-material/Link';
import SendIcon from '@mui/icons-material/Send';
import AdminPageLayout from '../components/admin/AdminPageLayout';

const STATUS_OPTIONS = ['ALL', 'PENDING', 'SENT', 'ACTIVE', 'ACCEPTED', 'REJECTED', 'EXPIRED'];
const STATUS_COLORS = {
  PENDING: 'warning', SENT: 'info', ACTIVE: 'info', ACCEPTED: 'success',
  REJECTED: 'error', EXPIRED: 'default',
};

const AdminQuotations = () => {
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [busyId, setBusyId] = useState(null);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  const apiBase = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const fetchQuotations = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${apiBase}/api/quotations`, { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to fetch quotations');
      setQuotations(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  useEffect(() => { fetchQuotations(); }, [fetchQuotations]);

  const notify = (message, severity = 'success') => setToast({ open: true, message, severity });

  // Fetch the normalized document (gives us the public share URL).
  const getDocument = async (id) => {
    const res = await fetch(`${apiBase}/api/quotations/${id}/document`, { headers: authHeaders() });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to build quote document');
    return data;
  };

  const handlePreview = async (id) => {
    setBusyId(id);
    try {
      const doc = await getDocument(id);
      window.open(doc.publicUrl, '_blank', 'noopener');
    } catch (err) {
      notify(err.message, 'error');
    } finally {
      setBusyId(null);
    }
  };

  const handleCopyLink = async (id) => {
    setBusyId(id);
    try {
      const doc = await getDocument(id);
      await navigator.clipboard.writeText(doc.publicUrl);
      notify('Quote link copied to clipboard');
    } catch (err) {
      notify(err.message, 'error');
    } finally {
      setBusyId(null);
    }
  };

  const handleSend = async (q) => {
    if (!q.customerEmail) {
      notify('This quote has no customer email on file.', 'error');
      return;
    }
    setBusyId(q.id);
    try {
      const res = await fetch(`${apiBase}/api/quotations/${q.id}/send`, {
        method: 'POST', headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send quote');
      notify(`Quote sent to ${data.recipient || q.customerEmail}`);
      fetchQuotations(); // refresh status (PENDING -> SENT)
    } catch (err) {
      notify(err.message, 'error');
    } finally {
      setBusyId(null);
    }
  };

  const filtered = statusFilter === 'ALL'
    ? quotations
    : quotations.filter((q) => q.status === statusFilter);

  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—');
  const customerName = (q) => [q.customerFirstName, q.customerLastName].filter(Boolean).join(' ') || '—';
  const money = (n) => (n === null || n === undefined || n === '' ? '—' : `$${Number(n).toFixed(2)}`);

  return (
    <AdminPageLayout title="Quotations" subtitle="Review, preview and send quotes to customers">
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={2}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="status-filter-label">Status</InputLabel>
          <Select labelId="status-filter-label" label="Status" value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}>
            {STATUS_OPTIONS.map((s) => <MenuItem key={s} value={s}>{s === 'ALL' ? 'All statuses' : s}</MenuItem>)}
          </Select>
        </FormControl>
        <Typography variant="body2" color="text.secondary">
          {filtered.length} quotation{filtered.length === 1 ? '' : 's'}
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Email</TableCell>
                <TableCell align="right">Price</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No quotations found.
                </TableCell></TableRow>
              ) : filtered.map((q) => {
                const busy = busyId === q.id;
                return (
                  <TableRow key={q.id} hover>
                    <TableCell>{fmtDate(q.createdAt)}</TableCell>
                    <TableCell>{customerName(q)}</TableCell>
                    <TableCell>{q.customerEmail || '—'}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{money(q.price)}</TableCell>
                    <TableCell>
                      <Chip size="small" label={q.status} color={STATUS_COLORS[q.status] || 'default'} />
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="Preview">
                          <span><IconButton size="small" disabled={busy} onClick={() => handlePreview(q.id)}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton></span>
                        </Tooltip>
                        <Tooltip title="Copy share link">
                          <span><IconButton size="small" disabled={busy} onClick={() => handleCopyLink(q.id)}>
                            <LinkIcon fontSize="small" />
                          </IconButton></span>
                        </Tooltip>
                        <Tooltip title={q.customerEmail ? 'Send to customer' : 'No customer email'}>
                          <span><Button size="small" variant="outlined" startIcon={<SendIcon />}
                            disabled={busy || !q.customerEmail} onClick={() => handleSend(q)}>
                            {q.status === 'PENDING' ? 'Send' : 'Resend'}
                          </Button></span>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Snackbar open={toast.open} autoHideDuration={4000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={toast.severity} onClose={() => setToast((t) => ({ ...t, open: false }))} variant="filled">
          {toast.message}
        </Alert>
      </Snackbar>
    </AdminPageLayout>
  );
};

export default AdminQuotations;
